/**
 * CreditExpiredModal - Hard gate at 0 Reader Credits
 * 
 * Shown when registered users attempt credit-consuming actions with 0 balance.
 * This modal BLOCKS the action until user converts to paid membership.
 */
import React from 'react';
import { X, AlertTriangle, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useReaderFunnel } from '@/contexts/ReaderFunnelContext';

export default function CreditExpiredModal() {
  const { 
    showCreditExpiredModal, 
    setShowCreditExpiredModal, 
    setShowPricingModal 
  } = useReaderFunnel();
  
  if (!showCreditExpiredModal) return null;
  
  const handleChooseMembership = () => {
    setShowCreditExpiredModal(false);
    setShowPricingModal(true);
  };
  
  const handleDismiss = () => {
    setShowCreditExpiredModal(false);
  };
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && handleDismiss()}
    >
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-red-500/30 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4 text-center">
          <button 
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-700/50 transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
          
          {/* NewAudia Logo */}
          <img 
            src="/newaudia-logo.jpg" 
            alt="NewAudia" 
            className="h-12 mx-auto mb-3 rounded-lg"
          />
          
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="p-3 bg-red-600/20 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
          </div>
          <h2 className="text-xl font-bold text-white">Complimentary Reader Credits Expired</h2>
        </div>
        
        {/* Content */}
        <div className="px-6 pb-6">
          <p className="text-slate-300 text-sm leading-relaxed mb-6">
            Your complimentary Reader Credits have expired. Please choose a membership level 
            to continue your journey.
          </p>
          
          {/* Visual indicator */}
          <div className="bg-slate-800/50 rounded-xl p-4 mb-6 text-center">
            <div className="text-4xl font-bold text-red-400 mb-1">0</div>
            <div className="text-sm text-slate-400">Reader Credits Remaining</div>
          </div>
          
          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleChooseMembership}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold py-3"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Choose Your Plan
            </Button>
            <Button
              onClick={handleDismiss}
              variant="ghost"
              className="w-full text-slate-400 hover:text-slate-300"
            >
              Not Now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
